import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isString from 'lodash/isString'
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import SvgCheck from '@Assets/SVG/Check'
import { useTranslation } from '@Globals/Translations'
import { FormElementProps, FormState } from '@Shared/Components/Forms/FormElement.types'
import { useFormContext } from '@Shared/Components/Forms/useFormContext'

import styles from './FormSelect.module.scss'

export interface FormSelectProps<T> extends FormElementProps{
  childElement?: (option, selected) => ReactNode
  deselectOption?: T
  deselectOptionTitle?: string
  disabled?: boolean
  errorClassName?: string
  label?: string
  onError?: (error?: string) => void
  optionKey?: (option: T | string) => string
  options: string[] | T[]
  showValidation?: boolean
}

/**
 * @deprecated The const should not be used
 */
export const FormSelectDeprecated:
FC<FormSelectProps<{}>> = ({ childElement,
  className,
  deselectOption,
  deselectOptionTitle,
  disabled,
  errorClassName,
  id,
  label,
  modelKey,
  onError,
  optionKey,
  options,
  showValidation = false,
  startTouched,
  triggersValidation,
  validations }) => {
  const { t } = useTranslation()
  const { runValidation, submitAttempts, updateValue, validateOnBlur, validation, value } = useFormContext(modelKey, validations)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const optionsRef = useRef<HTMLDivElement | null>(null)
  const [hasErrors, setHasErrors] = useState<boolean>(validation && validation.validity === 'ERROR')
  const [touched, setTouched] = useState(startTouched)

  const callOnError = (err: string | undefined) => {
    if (onError) {
      onError(err)
    }
  }

  const error: string | null = useMemo(() => {
    if (!validation?.validationResponses) {
      return null
    }
    const foundError = validation.validationResponses.find(v => v.message && v.state === FormState.ERROR)
    if (foundError?.message) {
      callOnError(foundError?.message)
      return foundError.message
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation])

  useEffect(() => {
    setHasErrors(validation && validation.validity === FormState.ERROR)
  }, [validation, value])

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(prev => !prev)
    }
  }
  const handleOptionClick = (option) => {
    setTouched(true)
    updateValue(option)
    validateOnBlur()
    setIsOpen(false)
  }

  useEffect(() => {
    if (isEmpty(triggersValidation) || disabled) {
      return
    }
    if (isEmpty(value)) {
      setTouched(false)
    } else if (!isEmpty(value)) {
      setTouched(true)
      runValidation(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, triggersValidation)

  useEffect(() => {
    if (submitAttempts >= 1) {
      setTouched(true)
      setHasErrors(validation && validation.validity === 'ERROR')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempts])

  useEffect(() => {
    const handleWindowClick = (event) => {
      if (containerRef.current && !containerRef.current!.contains(event.target)) {
        setIsOpen(false)
      } else if (optionsRef.current && optionsRef.current!.contains(event.target)) {
        setIsOpen(true)
      }
    }
    window.addEventListener('click', handleWindowClick)
    return () => {
      window.removeEventListener('click', handleWindowClick)
    }
  }, [containerRef, optionsRef])

  const labelElement = useMemo(() => {
    if (childElement) {
      return childElement(value, value)
    }
    return value
  }, [childElement, value])
  const showCheckmark = useMemo(() => showValidation && touched && !hasErrors, [hasErrors, showValidation, touched])

  return (
    <div className={className}>
      <div
        className={clsx(
          styles.formSelect,
          touched && !isOpen && hasErrors && [styles.error, errorClassName],
          disabled && styles.disabled,
          showValidation && touched && !hasErrors && styles.valid,
        )}
        ref={containerRef}
      >
        <button className={clsx(styles.select, disabled && styles.disabled)} onClick={toggleDropdown} tabIndex={-1} type="button">
          {label && (
            <p className={clsx(
              styles.label,
              !(isEmpty(value) || isEqual(value, deselectOption)) && styles.hasValue,
              disabled && styles.disabled,
            )}
            >
              {label}
            </p>
          )}
          <div className={clsx(styles.selectedOption, label && styles.hasLabel, disabled && styles.disabled)}>{labelElement}</div>
          <div className={clsx(styles.arrow, disabled && styles.disabled)}>
            <SvgAngleLeft className={clsx(
              isOpen && styles.isOpen,
              !isOpen && styles.isClosed,
              showCheckmark && styles.hideForCheck,
            )}
            />
            {showCheckmark && <div className={styles.checkmark}><SvgCheck /></div>}

          </div>
        </button>
        {isOpen && (
          <div className={styles.dropdown} ref={optionsRef} style={{ maxWidth: `${containerRef.current?.offsetWidth}px` }}>
            {deselectOption && (
              <button
                onClick={() => handleOptionClick(deselectOption)}
                type="button"
              >
                <span>{deselectOptionTitle}</span>
              </button>
            )}
            {options.map(option => (
              <button key={`${id}_${!isString(option) && optionKey ? optionKey(option) : option}`} onClick={() => handleOptionClick(option)} type="button">
                {childElement ? childElement(option, value) : option}
              </button>
            ))}
          </div>
        )}
      </div>
      {hasErrors && touched && error && (<p key={error} className={styles.errorCode}>{t(error)}</p>)}
    </div>
  )
}
